import { CgUser } from 'react-icons/cg'
import { FaFlag } from 'react-icons/fa6'
import { HiCheck, HiSwitchVertical } from 'react-icons/hi'

import { EmailIcon } from '@chakra-ui/icons'
import { Box, Icon, Spacer } from '@chakra-ui/react'

import {
  IncidentNote,
  OrderRepair,
  Share,
  SystemAction,
} from '@/components/icons'
import { CallEnd, CallStart, VoiceMessage } from '@/components/icons'
import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentActionType, IncidentStatus } from '@/graphql/generated/schemas'
import { creatorRoleName } from '@/utils/formats'

import {
  CannedIncidentActionType,
  ExtendedIncidentActionType,
  NonActionItem,
  PriorityChangeHistory,
} from '../types/types'
import { IncidentPriorityDropdown } from './IncidentPriorityDropdown'
import { IncidentStatusTag } from './IncidentStatus'

interface IncidentHeaderIProps {
  incident?: IncidentQuery['incident']
}

export const IncidentHeader = ({ incident }: IncidentHeaderIProps) => {
  return (
    <Box alignItems='center' direction='row' display='flex'>
      <Box as='span' fontWeight='semibold' mr='15px'>
        {incident?.name}
      </Box>
      <Spacer />
      <Box alignItems='center' display='flex' gap='8px' ml='5px'>
        <IncidentPriorityDropdown incident={incident} />
        <IncidentStatusTag status={incident?.status ?? IncidentStatus.Active} />
      </Box>
    </Box>
  )
}

interface ActionHeaderIProps {
  actionType:
    | IncidentActionType
    | NonActionItem
    | CannedIncidentActionType
    | ExtendedIncidentActionType
  username?: string
  roleName?: string
  systemMessage?: string
  technicianName?: string
  history?: PriorityChangeHistory
}

const formatPriorityValue = (value: string | null | undefined): string => {
  if (!value) return 'No Priority'
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

export const ActionHeader = ({
  actionType,
  username,
  roleName,
  systemMessage,
  technicianName,
  history,
}: ActionHeaderIProps) => {
  switch (actionType) {
    case IncidentActionType.AcknowledgementByGuard:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Acknowledged{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.TakeoverByGuard:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Took Over{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.AcknowledgementByOperator:
      return (
        <Box direction='row' display='flex'>
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#2E6CDB' fontWeight='bold'>
              Acknowledged{' '}
            </Box>
            the Incident.
          </Box>
          <Spacer />
          <Box ml='5px'>
            <IncidentStatusTag status={IncidentStatus.InProgress} />
          </Box>
        </Box>
      )
    case IncidentActionType.OnScene:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          is{' '}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            On Scene.
          </Box>
        </Box>
      )
    case IncidentActionType.ReleaseByGuard:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Released{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.ReleaseByOperator:
      return (
        <Box direction='row' display='flex'>
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#2E6CDB' fontWeight='bold'>
              Released{' '}
            </Box>
            the Incident.
          </Box>
          <Spacer />
          <Box ml='5px'>
            <IncidentStatusTag status={IncidentStatus.Active} />
          </Box>
        </Box>
      )
    case IncidentActionType.Cleared:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#13B0B0' fontWeight='bold'>
            Cleared{' '}
          </Box>
          the Incident.
        </Box>
      )
    case IncidentActionType.Resolved:
      return (
        <Box direction='row' display='flex'>
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#13B0B0' fontWeight='bold'>
              Resolved{' '}
            </Box>
            the Incident.
          </Box>
          <Spacer />
          <Box ml='5px'>
            <IncidentStatusTag status={IncidentStatus.Resolved} />
          </Box>
        </Box>
      )
    case IncidentActionType.OrderDeviceRepair:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#13B0B0' fontWeight='bold'>
            contacted Technician{' '}
          </Box>
          {technicianName}.
        </Box>
      )
    case IncidentActionType.SetRealToFalse:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          has marked the Incident as{' '}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            False.
          </Box>
        </Box>
      )
    case IncidentActionType.SetRealToTrue:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          has marked the Incident as{' '}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            Real.
          </Box>
        </Box>
      )
    case IncidentActionType.StartEmergencyCall:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          called Emergency Number
        </Box>
      )
    case IncidentActionType.EndEmergencyCall:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username}`}
          </Box>
          's Emergency Call ended
        </Box>
      )
    case IncidentActionType.Flag:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`AI Operator has `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            flagged{' '}
          </Box>
          the incident for further review and analysis.
        </Box>
      )
    case NonActionItem.IncidentNote:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          added a note.
        </Box>
      )
    case NonActionItem.SystemAction:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {systemMessage}
          </Box>
        </Box>
      )
    case NonActionItem.AudioMessage:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          sent a voice message.
        </Box>
      )
    case CannedIncidentActionType.Email:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`AI Operator `}
          </Box>
          {`sent an `}
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            {` email notification `}
          </Box>
          to {`${creatorRoleName(roleName)} ${username}`}
        </Box>
      )
    case IncidentActionType.SystemPriorityAssignment:
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            System changed priority from No Priority to{' '}
            {formatPriorityValue(history?.newValue)} based on incident category
            default
          </Box>
        </Box>
      )
    case ExtendedIncidentActionType.PriorityChange: {
      const oldPriority = history?.oldValue
      const newPriority = history?.newValue
      if (oldPriority === null || oldPriority === undefined) {
        return (
          <Box data-testid='ActionHeader:header'>
            <Box as='span' fontWeight='bold'>
              {`${creatorRoleName(roleName)} ${username} `}
            </Box>
            <Box as='span' color='#2E6CDB' fontWeight='bold'>
              set priority{' '}
            </Box>
            as{' '}
            <Box as='span' fontWeight='bold'>
              {formatPriorityValue(newPriority)}
            </Box>
            .
          </Box>
        )
      }
      return (
        <Box data-testid='ActionHeader:header'>
          <Box as='span' fontWeight='bold'>
            {`${creatorRoleName(roleName)} ${username} `}
          </Box>
          <Box as='span' color='#2E6CDB' fontWeight='bold'>
            changed the priority{' '}
          </Box>
          from{' '}
          <Box as='span' fontWeight='bold'>
            {formatPriorityValue(oldPriority)}
          </Box>{' '}
          to{' '}
          <Box as='span' fontWeight='bold'>
            {formatPriorityValue(newPriority)}
          </Box>
          .
        </Box>
      )
    }
  }
}

export const ActionIcon = (type: string) => {
  switch (type) {
    case IncidentActionType.AcknowledgementByGuard:
    case IncidentActionType.AcknowledgementByOperator:
    case IncidentActionType.TakeoverByGuard:
    case IncidentActionType.Cleared:
    case IncidentActionType.Resolved:
    case IncidentActionType.SetRealToTrue:
    case IncidentActionType.SetRealToFalse:
      return <HiCheck color='#fff' size='22' />
    case IncidentActionType.OnScene:
      return <CgUser color='#fff' size='22' />
    case IncidentActionType.OrderDeviceRepair:
      return <Icon as={OrderRepair} boxSize='18px' />
    case IncidentActionType.ReleaseByOperator:
      return <Icon as={Share} boxSize='18px' />
    case IncidentActionType.StartEmergencyCall:
      return <Icon as={CallStart} boxSize='18px' />
    case IncidentActionType.EndEmergencyCall:
      return <Icon as={CallEnd} boxSize='18px' />
    case IncidentActionType.Flag:
      return <FaFlag color='#fff' size='18' />
    case NonActionItem.IncidentNote:
      return <Icon as={IncidentNote} boxSize='18px' />
    case NonActionItem.SystemAction:
    case IncidentActionType.SystemPriorityAssignment:
      return <Icon as={SystemAction} boxSize='26px' />
    case NonActionItem.AudioMessage:
      return <Icon as={VoiceMessage} boxSize='18px' />
    case CannedIncidentActionType.Email:
      return <Icon as={EmailIcon} boxSize='18px' color='#fff' />
    case ExtendedIncidentActionType.PriorityChange:
    case 'PRIORITY_CHANGE':
      return <HiSwitchVertical color='#fff' size='20' />
  }
}
